import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  XCircle,
  ExternalLink,
  Trash2,
  RefreshCcw,
  FolderCog,
  Plus,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import FolderPickerDialog from "@/components/FolderPickerDialog";

function StatusPill({ ok, okText = "Connected", noText = "Not connected" }) {
  return ok ? (
    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
      <CheckCircle2 className="w-3.5 h-3.5" /> {okText}
    </Badge>
  ) : (
    <Badge variant="secondary" className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100">
      <XCircle className="w-3.5 h-3.5" /> {noText}
    </Badge>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [drive, setDrive] = useState({ connected: false, connection: null });
  const [up, setUp] = useState({ configured: false, config: null, accounts: [] });
  const [apiKey, setApiKey] = useState("");
  const [savingUp, setSavingUp] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [driveError, setDriveError] = useState(null);

  const refresh = async () => {
    const [d, u] = await Promise.all([
      api.get("/drive/status"),
      api.get("/integrations/uploadpost"),
    ]);
    setDrive(d.data);
    setUp(u.data);
  };

  const refreshAll = async ({ silent = false } = {}) => {
    setRefreshingAll(true);
    try {
      await api.post("/integrations/uploadpost/refresh");
      if (!silent) toast.success("TikTok statuses refreshed");
      await refresh();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to refresh");
    } finally {
      setRefreshingAll(false);
    }
  };

  useEffect(() => {
    (async () => {
      await refresh();
      if (params.get("up") === "connected") {
        params.delete("up");
        setParams(params, { replace: true });
        await refreshAll({ silent: false });
      } else if (params.get("drive") === "connected") {
        toast.success("Google Drive connected");
        params.delete("drive");
        setParams(params, { replace: true });
      } else if (params.get("drive") === "error") {
        const msg = params.get("msg") || "unknown";
        setDriveError(msg);
        toast.error(`Drive connect error: ${msg}`);
        params.delete("drive");
        params.delete("msg");
        setParams(params, { replace: true });
      }
    })();
  }, []);

  const connectDrive = async () => {
    setDriveError(null);
    setConnectingDrive(true);
    try {
      const { data } = await api.get("/auth/google/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Google OAuth");
      setConnectingDrive(false);
    }
  };

  const disconnectDrive = async () => {
    await api.delete("/drive/disconnect");
    toast.success("Google Drive disconnected");
    await refresh();
  };

  const saveApiKey = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      toast.error("API key is required");
      return;
    }
    setSavingUp(true);
    try {
      await api.post("/integrations/uploadpost", { api_key: apiKey.trim() });
      toast.success("Publishing key saved");
      setApiKey("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save API key");
    } finally {
      setSavingUp(false);
    }
  };

  const removeUp = async () => {
    if (!window.confirm("Remove the publishing key and all TikTok accounts?")) return;
    await api.delete("/integrations/uploadpost");
    toast.success("Publishing configuration cleared");
    await refresh();
  };

  const openAdd = () => {
    setNewLabel("");
    setAddOpen(true);
  };

  const addAccount = async () => {
    if (!newLabel.trim()) {
      toast.error("Give the account a label");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/integrations/uploadpost/accounts", { label: newLabel.trim() });
      setAddOpen(false);
      toast.success(`Added “${data.account.label}”. Now click Connect to link the TikTok.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add account");
    } finally {
      setAdding(false);
    }
  };

  const connectAccount = async (id, platform = "tiktok") => {
    try {
      const { data } = await api.post(`/integrations/uploadpost/accounts/${id}/connect`, null, { params: { platform } });
      window.open(data.access_url, "_blank", "noopener,noreferrer");
      const label = platform === "tiktok" ? "TikTok" : "Instagram";
      toast.info(`Complete the ${label} linking in the new tab, then return and refresh.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start linking");
    }
  };

  const refreshAccount = async (id) => {
    try {
      const { data } = await api.post(`/integrations/uploadpost/accounts/${id}/refresh`);
      const linked = [];
      if (data.tiktok_connected) linked.push("TikTok");
      if (data.instagram_connected) linked.push("Instagram");
      if (linked.length) toast.success(`Linked: ${linked.join(" + ")}`);
      else toast.info("Nothing linked yet on this account");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to refresh");
    }
  };

  const removeAccount = async (id, label) => {
    if (!window.confirm(`Remove account “${label}”?`)) return;
    try {
      await api.delete(`/integrations/uploadpost/accounts/${id}`);
      toast.success("Account removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const pickRoot = async (folder) => {
    try {
      await api.put("/drive/root", { folder_id: folder.id, folder_name: folder.name });
      toast.success(`Source folder set to “${folder.name}”`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set source folder");
    }
  };

  const clearRoot = async () => {
    await api.put("/drive/root", { folder_id: null });
    toast.success("Source folder cleared");
    await refresh();
  };

  const upConfigured = up.configured;
  const sharedMode = !!up.shared_master;
  const isAdmin = !!user?.is_admin;
  // API-key management is admin-only. Regular users never see the key form
  // (Shared Master Key mode is enforced app-wide). Even the admin manages the
  // key on /admin/settings — keep the legacy per-user form hidden here.
  const showKeyForm = false;
  const accounts = up.accounts || [];
  const rootFolderName = drive.connection?.root_folder_name;

  return (
    <div className="space-y-8" data-testid="integrations-page">
      <header>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Setup</div>
        <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">Integrations</h1>
        <p className="mt-1.5 text-sm sm:text-base text-slate-600">Connect Google Drive and one or more TikTok / Instagram accounts.</p>
      </header>

      <Card className="p-4 sm:p-6 border-zinc-200" data-testid="section-drive">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Step 1</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900">Google Drive</div>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Grants private read-only access. Files are downloaded server-side over the Drive API — no public links required.
            </p>
          </div>
          <StatusPill ok={drive.connected} />
        </div>

        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="drive-privacy-note">
          <div className="font-semibold text-emerald-950">Your Drive stays private</div>
          <ul className="mt-1.5 space-y-1 list-disc pl-5">
            <li>Only <span className="font-medium">you</span> can browse your Drive here — no other PostQik user, not even the admin, can see your files.</li>
            <li>PostQik requests <code className="font-mono">drive.readonly</code> scope — we can read your files but never modify, delete or share them.</li>
            <li>Files are streamed from Drive to TikTok inside our backend for the moment of the send only — they are never stored on our servers.</li>
            <li>Disconnecting revokes our access with Google immediately.</li>
          </ul>
        </div>

        {driveError && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="drive-error-block">
            <div className="font-semibold">Drive connect failed</div>
            <div className="mt-1 break-words text-red-800 text-xs font-mono">{driveError}</div>
            <div className="mt-2 text-xs text-red-900">
              Common fixes:
              <ul className="mt-1 list-disc pl-5 space-y-0.5">
                <li>Add your Gmail as a <span className="font-medium">Test User</span> in Google Cloud Console → Google Auth Platform → Audience.</li>
                <li>Testing-mode refresh tokens expire after 7 days; a fresh connect fixes it once you re-authorise.</li>
                <li>Verify the OAuth client&apos;s <span className="font-medium">Authorized redirect URI</span> is <code className="font-mono">/api/auth/google/callback</code> under our domain.</li>
                <li>If Google shows <span className="font-medium">Access blocked</span>, your account is not a Test User or the app needs verification for the Drive scope.</li>
              </ul>
            </div>
            <button
              type="button"
              className="mt-2 text-xs text-red-800 underline underline-offset-2"
              onClick={() => setDriveError(null)}
              data-testid="dismiss-drive-error"
            >
              Dismiss
            </button>
          </div>
        )}


        <div className="mt-6 flex flex-wrap gap-2">
          {!drive.connected ? (
            <Button
              onClick={connectDrive}
              disabled={connectingDrive}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="connect-drive-button"
            >
              {connectingDrive ? "Redirecting…" : "Connect Google Drive"} <ExternalLink className="w-4 h-4" />
            </Button>
          ) : (
            <Button variant="outline" onClick={disconnectDrive} data-testid="disconnect-drive-button">
              <Trash2 className="w-4 h-4" /> Disconnect
            </Button>
          )}
        </div>
        {drive.connected && drive.connection?.connected_at && (
          <div className="mt-4 text-xs text-slate-500 tabular-nums">
            Connected {new Date(drive.connection.connected_at).toLocaleString()}
          </div>
        )}

        {drive.connected && (
          <div className="mt-6 pt-6 border-t border-zinc-200" data-testid="drive-root-block">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Step 1b</div>
                <div className="mt-1 font-display text-lg font-semibold text-slate-900">Source folder</div>
                <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                  Limits the Drive Browser to a single folder (and its subfolders). Leave empty to browse everything in My Drive.
                </p>
                <div className="mt-3 text-sm">
                  {rootFolderName ? (
                    <span>Currently: <span className="font-medium text-slate-900">{rootFolderName}</span></span>
                  ) : (
                    <span className="text-slate-500">Currently: everywhere in My Drive</span>
                  )}
                </div>
              </div>
              <StatusPill ok={!!rootFolderName} okText="Scoped" noText="Unscoped" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPickerOpen(true)} data-testid="pick-root-folder-button">
                <FolderCog className="w-4 h-4" /> {rootFolderName ? "Change folder" : "Choose folder"}
              </Button>
              {rootFolderName && (
                <Button variant="ghost" onClick={clearRoot} className="text-slate-600" data-testid="clear-root-folder-button">
                  <Trash2 className="w-4 h-4" /> Clear scope
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 border-zinc-200" data-testid="section-uploadpost">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Step 2</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900">Social accounts</div>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Add one account per channel and link its TikTok and/or Instagram. Each account keeps its logins separate so publishing to different brands stays clean.
            </p>
          </div>
          {isAdmin && (
            <StatusPill ok={upConfigured} okText="Key saved" noText="No key" />
          )}
        </div>

        {!upConfigured && isAdmin && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-key-admin-notice">
            No master publishing key set yet. Configure it at <a href="/admin/settings" className="underline underline-offset-2 font-medium">Admin → Settings</a> so testers can link TikTok accounts.
          </div>
        )}

        {showKeyForm && (
          <form onSubmit={saveApiKey} className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor="up-key" className="text-slate-700">Publishing API key</Label>
              <Input
                id="up-key"
                placeholder={upConfigured ? `Update key (current ${up.config?.api_key_masked || "•••"})` : "Apikey_xxxxxxxx"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                type="password"
                autoComplete="off"
                data-testid="up-apikey-input"
              />
            </div>
            <Button type="submit" disabled={savingUp} className="bg-zinc-900 hover:bg-zinc-800 text-white h-9" data-testid="save-uploadpost-button">
              {savingUp ? "Saving…" : upConfigured ? "Update key" : "Save key"}
            </Button>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-zinc-200" data-testid="tiktok-accounts-block">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Step 2b</div>
              <div className="mt-1 font-display text-lg font-semibold text-slate-900">Linked accounts</div>
              <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                Add as many accounts as you like. On the Drive Browser you&apos;ll pick which one and which platform each post is sent to.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => refreshAll({ silent: false })} disabled={refreshingAll || accounts.length === 0} data-testid="refresh-all-accounts">
                <RefreshCcw className="w-4 h-4" /> {refreshingAll ? "Refreshing…" : "Refresh all"}
              </Button>
              <Button size="sm" onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-account-button">
                <Plus className="w-4 h-4" /> Add account
              </Button>
            </div>
          </div>

          <div className="mt-4 border border-zinc-200 rounded-md overflow-hidden">
            {accounts.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center" data-testid="no-accounts">
                No accounts yet. Click <span className="font-medium">Add account</span> to link one.
              </div>
            ) : (
              accounts.map((a) => (
                <div key={a.id} className="flex flex-col gap-3 px-4 py-3 border-b border-zinc-100 last:border-b-0" data-testid={`account-row-${a.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{a.label}</div>
                      <div className="text-[11px] text-slate-500 tabular-nums mt-0.5 break-all">
                        <code className="font-mono">{a.username}</code>
                        {a.last_status_check && <> • checked {new Date(a.last_status_check).toLocaleString()}</>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => refreshAccount(a.id)} data-testid={`refresh-account-${a.id}`} aria-label="Refresh status">
                        <RefreshCcw className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeAccount(a.id, a.label)} className="text-slate-500" data-testid={`remove-account-${a.id}`} aria-label="Remove account">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
                    <PlatformRow
                      icon="TT"
                      label="TikTok"
                      linked={!!a.tiktok_connected}
                      onConnect={() => connectAccount(a.id, "tiktok")}
                      testIdBase={`account-${a.id}-tiktok`}
                      badgeClass="bg-black text-white"
                    />
                    <PlatformRow
                      icon="IG"
                      label="Instagram"
                      linked={!!a.instagram_connected}
                      onConnect={() => connectAccount(a.id, "instagram")}
                      testIdBase={`account-${a.id}-instagram`}
                      badgeClass="bg-gradient-to-br from-purple-500 to-pink-500 text-white"
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className={`mt-4 ${(sharedMode || !isAdmin) ? "hidden" : ""}`}>
            <Button type="button" variant="ghost" onClick={removeUp} className="text-slate-500 text-xs" data-testid="delete-uploadpost-button">
              Clear API key and all accounts
            </Button>
          </div>
        </div>
      </Card>

      <FolderPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onPick={pickRoot} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add TikTok account</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-label" className="text-slate-700">Label</Label>
            <Input
              id="new-label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Main brand, Side channel"
              data-testid="add-account-label-input"
              autoFocus
            />
            <p className="text-xs text-slate-500">Just a name for you to identify this account in the app.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addAccount} disabled={adding} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-account-confirm">
              {adding ? "Creating…" : "Create & link next"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlatformRow({ icon, label, linked, onConnect, testIdBase, badgeClass }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-zinc-100 bg-white p-2" data-testid={`${testIdBase}-row`}>
      <div className={`shrink-0 rounded-md w-8 h-8 flex items-center justify-center text-[10px] font-bold tracking-wide ${badgeClass}`} aria-hidden="true">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-slate-900">{label}</div>
        <div className={`text-[11px] ${linked ? "text-emerald-700" : "text-slate-500"}`} data-testid={`${testIdBase}-status`}>
          {linked ? "Linked" : "Not linked"}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onConnect} data-testid={`${testIdBase}-connect`}>
        {linked ? "Re-link" : "Connect"}
      </Button>
    </div>
  );
}
